import { Container } from '@chronosphereio/chrono-ui';
import { forwardRef, useId } from 'react';
import { getFormattedPanelKey } from '@/plugin-system/components/VizPanel/utils';
import {
  usePanelKey,
  useVizPanelColor,
  VizPanelContextProvider,
} from '@/plugin-system/components/VizPanelContextProvider';
import { VizContainerContextProvider, VizContainerPanelDefinitionContextProvider } from '../context';
import { useVizContainerLayout, useVizContainerMenuItems, useVizContainerPanelInspector } from '../hooks/internal';
import { normalizeVizContainerColor } from './colorUtils';
import {
  DEFAULT_VIZ_CONTAINER_SUBTLE_ACTIONS_VISIBILITY,
  DEFAULT_VIZ_CONTAINER_VARIANT,
  VizContainerInternalProps,
  VizContainerRootBaseProps,
} from './model';

/**
 * Root component that contains all the parts of a `VizContainer`. It extends from the chrono-ui `Container` component
 * and shares similar props and behavior, when possible, for consistency.
 *
 * This is the "base" version of the root component that comes with very minimal features and dependencies on context.
 * Instead use `Root` if you want a more feature-rich root component.
 */
export const VizContainerRootBase = forwardRef<HTMLDivElement, VizContainerRootBaseProps>(function VizContainer(
  {
    panelKey: initPanelKey,
    disablePadding = false,
    variant = DEFAULT_VIZ_CONTAINER_VARIANT,
    panelDefinition,
    ...otherProps
  }: VizContainerRootBaseProps,
  ref
) {
  // Support auto-setting a panel key for consumers when one is not passed to make it very easy for consumers to
  // get this working quickly in cases where they do not care about managing the panel key.
  const fallbackPanelKey = useId();
  const panelKey = initPanelKey || fallbackPanelKey;

  return (
    <VizPanelContextProvider panelKey={panelKey}>
      <VizContainerContextProvider variant={variant} disablePadding={disablePadding}>
        <VizContainerPanelDefinitionContextProvider panelDefinition={panelDefinition}>
          <VizContainerInternal ref={ref} {...otherProps} />
        </VizContainerPanelDefinitionContextProvider>
      </VizContainerContextProvider>
    </VizPanelContextProvider>
  );
});

/**
 * Component that contains the majority of the functionality for `VizContainer`. A separate component is needed in order
 * to wrap the component in `VizPanelContextProvider` and access the context when managing how it renders.
 */
export const VizContainerInternal = forwardRef<HTMLDivElement, VizContainerInternalProps>(function VizContainerInternal(
  {
    children,
    subtleActionsVisibility = DEFAULT_VIZ_CONTAINER_SUBTLE_ACTIONS_VISIBILITY,
    color: vizContainerColor,
    backgroundColor: vizContainerBackgroundColor,
    margin,
    maxHeight,
    menuItems: initMenuItems,
    panelInspectorContent,
    id,
    ...containerProps
  }: VizContainerInternalProps,
  ref
) {
  const panelKey = usePanelKey();
  const layout = useVizContainerLayout();
  const { color: vizPanelColor } = useVizPanelColor();
  const color = normalizeVizContainerColor(vizContainerColor, vizPanelColor.color);
  const backgroundColor = normalizeVizContainerColor(vizContainerBackgroundColor, vizPanelColor.background);

  const panelInspector = useVizContainerPanelInspector({ panelInspectorContent });
  const menuItems = useVizContainerMenuItems({ menuItems: initMenuItems, panelInspector });

  return (
    <>
      <Container
        ref={ref}
        {...containerProps}
        id={id ?? getFormattedPanelKey(panelKey)}
        actionsSize="small"
        subtleActionsVisibility={subtleActionsVisibility}
        menuItems={menuItems}
        sx={{
          margin,
          maxHeight,
          paddingY: `${layout.container.paddingY}px`,
          paddingX: `${layout.container.paddingX}px`,

          color: color,
          background: backgroundColor,

          '& .container-body-inner': {
            // !important is working around an edge case in `Container` where this padding will get overridden incorrectly
            // when a `VizContainer.Root` is set up with just a `VizContainer.Body`, leading to some missing vertical padding.
            paddingY: `${layout.containerBodyInner.paddingY}px !important`,
            paddingX: `${layout.containerBodyInner.paddingX}px`,

            borderBottomLeftRadius: `${layout.containerBodyInner.borderBottomLeftRadius}px`,
            borderBottomRightRadius: `${layout.containerBodyInner.borderBottomRightRadius}px`,
          },

          '& .container-header': {
            // Adjustment to the 100% width set on `Container.Header` that plays properly with the margins
            // used below. The previous setting of `auto` broke some edge cases.
            width: `calc(100% - ${layout.header.marginX}px * 2)`,

            marginY: `${layout.header.marginY}px`,
            marginX: `${layout.header.marginX}px`,
          },
          '& .container-header, & .floating-actions': {
            '& svg': {
              // Change icon colors to match the text color when the background and color are set.
              // For now, this is a relatively wide css selector. It may need tweaking if we find edge cases where it is
              // accidentally targeting svgs that should not be modified.
              color: backgroundColor !== undefined ? color : undefined,
            },
          },
        }}
      >
        {children}
      </Container>
      {panelInspector.panelInspector}
    </>
  );
});
